import React, { useState } from 'react';
import {
  CheckCircle,
  XCircle,
  Loader2,
  CircleDashed,
  ChevronDown,
} from 'lucide-react';

import { useTaskPlan } from '../../context/TaskPlanProvider';
import { RECENT_GOAL_COUNT } from '../../utils/constants';

// Helper component (unchanged)
const StatusIcon = ({ status }) => {
  switch (status) {
    case 'in progress':
      return <Loader2 className="h-4 w-4 animate-spin text-blue-400" />;
    case 'done':
      return <CheckCircle className="h-4 w-4 text-emerald-400" />;
    case 'cancelled':
      return <XCircle className="h-4 w-4 text-red-400" />;
    case 'todo':
    default:
      return <CircleDashed className="h-4 w-4 text-zinc-500" />;
  }
};

// --- NEW: A dedicated sub-component for rendering a single goal ---
// This keeps the main component logic clean.
const GoalItem = ({ goal, isExpanded, onToggleClick }) => (
  <div key={goal.goal_id}>
    {/* Clickable Goal Header Row */}
    {/* eslint-disable-next-line */}
    <div
      className="flex cursor-pointer items-center justify-between gap-3 px-4 py-3 transition-colors duration-200 hover:bg-zinc-800"
      onClick={() => onToggleClick(goal.goal_id)}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="w-4 flex-shrink-0">
          <StatusIcon status={goal.status} />
        </div>
        <p className="flex-1 truncate text-xs font-medium text-zinc-300">
          {goal.description}
        </p>
      </div>
      <ChevronDown
        className={`h-5 w-5 text-zinc-500 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
      />
    </div>

    {/* Collapsible Actions List */}
    <div
      className="grid transition-all duration-300 ease-in-out"
      style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
    >
      <div className="overflow-hidden">
        <div className="border-t border-zinc-800/50 bg-black/10 pb-2 pl-8 pr-4 pt-1">
          {goal.actions.map((action) => (
            <div
              key={action.action_id}
              className="flex items-center gap-3 py-2"
            >
              <div className="w-4 flex-shrink-0">
                <StatusIcon status={action.status} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-zinc-400">{action.description}</p>
                {action.type === 'tool_call' && action.tool_name && (
                  <span className="mt-1 inline-block rounded-md bg-zinc-700/80 px-1.5 py-0.5 font-mono text-[10px] text-zinc-400">
                    {action.tool_name}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const TaskPlan = ({ showAll }) => {
  const { plan } = useTaskPlan();
  // State now holds only the ID of the single expanded goal, or null.
  const [expandedGoalId, setExpandedGoalId] = useState(null);

  const toggleGoal = (goalId) => {
    // If the clicked goal is already open, close it (set to null).
    // Otherwise, open the new one.
    setExpandedGoalId((prevId) => (prevId === goalId ? null : goalId));
  };

  if (!plan || plan.length === 0) {
    return null;
  }

  // --- MODIFIED: Split the plan into two distinct groups ---
  const hasExtraGoals = plan.length > RECENT_GOAL_COUNT;
  const recentGoals = hasExtraGoals ? plan.slice(-RECENT_GOAL_COUNT) : plan;
  const extraGoals = hasExtraGoals
    ? plan.slice(0, plan.length - RECENT_GOAL_COUNT)
    : [];

  return (
    <div>
      {/* This container animates with a constant speed, avoiding the "stretchy" feel */}
      {hasExtraGoals && (
        <div
          className={`
            grid transition-[grid-template-rows] duration-300 ease-in-out
            ${showAll ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}
          `}
        >
          <div className="overflow-hidden">
            <div className="divide-y divide-zinc-800/50 border-b border-zinc-800/50">
              {extraGoals.map((goal) => (
                <GoalItem
                  key={goal.goal_id}
                  goal={goal}
                  isExpanded={expandedGoalId === goal.goal_id}
                  onToggleClick={toggleGoal}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* This container for "recent" goals is always visible and not animated */}
      <div className="divide-y divide-zinc-800/50">
        {recentGoals.map((goal) => (
          <GoalItem
            key={goal.goal_id}
            goal={goal}
            isExpanded={expandedGoalId === goal.goal_id}
            onToggleClick={toggleGoal}
          />
        ))}
      </div>
    </div>
  );
};

export default TaskPlan;
